<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <!-- Animated Background -->
    <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
    <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
    
    <!-- Floating Particles -->
    <div class="fixed inset-0 pointer-events-none">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <div class="max-w-2xl w-full space-y-8 relative z-10">
      <!-- Register Card -->
      <div class="bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
        <!-- Header -->
        <div class="text-center mb-8">
          <div class="flex justify-center mb-6">
            <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-16 h-16 rounded-2xl shadow-lg shadow-emerald-500/25" />
          </div>
          <h2 class="text-3xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
            Join OilBuddy AI
          </h2>
          <p class="text-slate-400">
            Create your account and start building your oilfield business
          </p>
        </div>

      <!-- User Type Selection -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-lg font-semibold text-white mb-4">I am a...</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            @click="selectUserType('contractor')"
            :class="form.userType === 'contractor' ? 'ring-2 ring-emerald-500 bg-emerald-500/20 border-emerald-500' : 'hover:bg-slate-700/50'"
            class="p-4 border-2 border-slate-600 rounded-lg text-left transition-all duration-200 bg-slate-800/50"
          >
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <h4 class="font-medium text-white">Contractor</h4>
                <p class="text-sm text-slate-400">1099 — find jobs, get paid</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            @click="selectUserType('employee')"
            :class="form.userType === 'employee' ? 'ring-2 ring-emerald-500 bg-emerald-500/20 border-emerald-500' : 'hover:bg-slate-700/50'"
            class="p-4 border-2 border-slate-600 rounded-lg text-left transition-all duration-200 bg-slate-800/50"
          >
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a4 4 0 00-4 4v1H5a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4z"/>
                </svg>
              </div>
              <div>
                <h4 class="font-medium text-white">W-2 Employee</h4>
                <p class="text-sm text-slate-400">Company payroll &amp; hats</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            @click="selectUserType('company')"
            :class="form.userType === 'company' ? 'ring-2 ring-emerald-500 bg-emerald-500/20 border-emerald-500' : 'hover:bg-slate-700/50'"
            class="p-4 border-2 border-slate-600 rounded-lg text-left transition-all duration-200 bg-slate-800/50"
          >
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <h4 class="font-medium text-white">Company</h4>
                <p class="text-sm text-slate-400">Hire &amp; run W-2 / 1099</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- Registration Form -->
      <form v-if="form.userType" class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 space-y-6" @submit.prevent="handleRegister">
          <!-- Personal Information -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label for="firstName" class="form-label">First Name *</label>
              <input
                id="firstName"
                v-model="form.firstName"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-500': errors.firstName }"
                placeholder="Enter your first name"
              />
              <p v-if="errors.firstName" class="text-red-400 text-sm mt-1">
                {{ errors.firstName }}
              </p>
            </div>

            <div class="form-group">
              <label for="lastName" class="form-label">Last Name *</label>
              <input
                id="lastName"
                v-model="form.lastName"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-500': errors.lastName }"
                placeholder="Enter your last name"
              />
              <p v-if="errors.lastName" class="text-red-400 text-sm mt-1">
                {{ errors.lastName }}
              </p>
            </div>
          </div>

          <!-- Company Name (for companies) -->
          <div v-if="form.userType === 'company'" class="form-group">
            <label for="companyName" class="form-label">Company Name *</label>
            <input
              id="companyName"
              v-model="form.companyName"
              type="text"
              required
              class="form-input"
                              :class="{ 'border-red-500': errors.companyName }"
              placeholder="Enter your company name"
            />
            <p v-if="errors.companyName" class="text-red-400 text-sm mt-1">
              {{ errors.companyName }}
            </p>
          </div>

          <!-- W-2 employee: invite + crew level -->
          <div v-if="form.userType === 'employee'" class="space-y-4">
            <div class="form-group">
              <label for="inviteCode" class="form-label">Company Invite Code *</label>
              <input
                id="inviteCode"
                v-model="formExtended.inviteCode"
                type="text"
                required
                class="form-input uppercase tracking-wider"
                :class="{ 'border-red-500': errors.inviteCode }"
                placeholder="e.g. OB0002B80C"
              />
              <p class="text-xs text-slate-500 mt-1">Get this from your company admin (W-2 Dashboard).</p>
              <p v-if="errors.inviteCode" class="text-red-400 text-sm mt-1">{{ errors.inviteCode }}</p>
            </div>

            <div class="form-group">
              <label class="form-label">I am a… *</label>
              <p class="text-xs text-slate-500 mb-3">Select your crew level / hat</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  v-for="opt in crewLevelOptions"
                  :key="opt.value"
                  type="button"
                  @click="formExtended.crewLevel = opt.value"
                  :class="formExtended.crewLevel === opt.value
                    ? 'ring-2 ring-emerald-500 bg-emerald-500/15 border-emerald-500'
                    : 'border-slate-600 hover:bg-slate-700/40'"
                  class="p-3 border-2 rounded-xl text-left transition-all bg-slate-800/40"
                >
                  <div class="font-medium text-white">{{ opt.label }}</div>
                  <div class="text-xs text-slate-400 mt-0.5">{{ opt.hint }}</div>
                </button>
              </div>
              <p v-if="errors.crewLevel" class="text-red-400 text-sm mt-2">{{ errors.crewLevel }}</p>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="form-group">
            <label for="email" class="form-label">Email Address *</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="form-input"
                              :class="{ 'border-red-500': errors.email }"
              placeholder="Enter your email address"
            />
                          <p v-if="errors.email" class="text-red-400 text-sm mt-1">
              {{ errors.email }}
            </p>
          </div>

          <div class="form-group">
            <label for="phone" class="form-label">Phone Number</label>
            <input
              id="phone"
              v-model="formExtended.phone"
              type="tel"
              class="form-input"
                              :class="{ 'border-red-500': errors.phone }"
              placeholder="Enter your phone number"
            />
                          <p v-if="errors.phone" class="text-red-400 text-sm mt-1">
              {{ errors.phone }}
            </p>
          </div>

          <!-- Password Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label for="password" class="form-label">Password *</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                required
                class="form-input"
                :class="{ 'border-red-500': errors.password }"
                placeholder="Create a password"
              />
              <p v-if="errors.password" class="text-red-400 text-sm mt-1">
                {{ errors.password }}
              </p>
              <p class="text-xs text-slate-400 mt-1">Must be at least 8 characters</p>
            </div>

            <div class="form-group">
              <label for="passwordConfirmation" class="form-label">Confirm Password *</label>
              <input
                id="passwordConfirmation"
                v-model="form.passwordConfirmation"
                type="password"
                required
                class="form-input"
                :class="{ 'border-red-500': errors.passwordConfirmation }"
                placeholder="Confirm your password"
              />
              <p v-if="errors.passwordConfirmation" class="text-red-400 text-sm mt-1">
                {{ errors.passwordConfirmation }}
              </p>
            </div>
          </div>

          <!-- Contractor-specific fields -->
          <div v-if="form.userType === 'contractor'" class="space-y-4">
                          <h4 class="font-medium text-white">Contractor Information</h4>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label for="ein" class="form-label">EIN (Optional)</label>
                <input
                  id="ein"
                  v-model="formExtended.ein"
                  type="text"
                  class="form-input"
                  placeholder="XX-XXXXXXX"
                />
              </div>

              <div class="form-group">
                <label for="ssn" class="form-label">SSN (Optional)</label>
                <input
                  id="ssn"
                  v-model="formExtended.ssn"
                  type="text"
                  class="form-input"
                  placeholder="XXX-XX-XXXX"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="address" class="form-label">Address</label>
              <input
                id="address"
                v-model="formExtended.address"
                type="text"
                class="form-input"
                placeholder="Enter your address"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="form-group">
                <label for="city" class="form-label">City</label>
                <input
                  id="city"
                  v-model="formExtended.city"
                  type="text"
                  class="form-input"
                  placeholder="City"
                />
              </div>

              <div class="form-group">
                <label for="state" class="form-label">State</label>
                <select v-model="formExtended.state" class="form-select">
                  <option value="">Select State</option>
                  <option value="ND">North Dakota</option>
                  <option value="MT">Montana</option>
                  <option value="SD">South Dakota</option>
                  <option value="WY">Wyoming</option>
                  <option value="TX">Texas</option>
                  <option value="OK">Oklahoma</option>
                  <option value="NM">New Mexico</option>
                  <option value="CO">Colorado</option>
                </select>
              </div>

              <div class="form-group">
                <label for="zipCode" class="form-label">ZIP Code</label>
                <input
                  id="zipCode"
                  v-model="formExtended.zipCode"
                  type="text"
                  class="form-input"
                  placeholder="ZIP Code"
                />
              </div>
            </div>
          </div>

          <!-- Company-specific fields -->
          <div v-if="form.userType === 'company'" class="space-y-4">
                          <h4 class="font-medium text-white">Company Information</h4>
            
            <div class="form-group">
              <label for="ein" class="form-label">EIN *</label>
              <input
                id="ein"
                v-model="formExtended.ein"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-500': errors.ein }"
                placeholder="XX-XXXXXXX"
              />
              <p v-if="errors.ein" class="text-red-400 text-sm mt-1">
                {{ errors.ein }}
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label for="industry" class="form-label">Industry</label>
                <select v-model="formExtended.industry" class="form-select">
                  <option value="">Select Industry</option>
                  <option value="exploration_production">Exploration & Production</option>
                  <option value="flowback">Flowback</option>
                  <option value="well_services">Well Services</option>
                  <option value="completions">Completions</option>
                  <option value="drilling">Drilling</option>
                  <option value="pipeline">Pipeline</option>
                  <option value="refining">Refining</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div class="form-group">
                <label for="companySize" class="form-label">Company Size</label>
                <select v-model="formExtended.companySize" class="form-select">
                  <option value="">Select Size</option>
                  <option value="small">1-10 employees</option>
                  <option value="medium">11-50 employees</option>
                  <option value="large">51-200 employees</option>
                  <option value="enterprise">200+ employees</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Terms and Conditions -->
          <div class="flex items-start space-x-3">
            <input
              id="acceptTerms"
              v-model="form.acceptTerms"
              type="checkbox"
              required
              class="form-checkbox mt-1"
            />
                          <label for="acceptTerms" class="text-sm text-slate-300">
              I agree to the 
                              <router-link to="/terms" class="text-emerald-400 hover:text-emerald-300" target="_blank">Terms of Service</router-link>
              and 
                              <router-link to="/privacy" class="text-emerald-400 hover:text-emerald-300" target="_blank">Privacy Policy</router-link>
            </label>
          </div>

          <!-- Submit Button -->
          <div>
            <button
              type="submit"
              :disabled="isLoading"
              class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                v-if="isLoading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isLoading ? 'Creating Account...' : 'Create Account' }}
            </button>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <div class="flex">
              <svg class="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm8.707-7.293a1 1 0 00-1.414-1.414L11 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <p class="text-sm text-red-400">{{ errorMessage }}</p>
            </div>
          </div>
      </form>

      <!-- Sign In Link -->
      <div class="text-center">
        <p class="text-sm text-slate-400">
          Already have an account?
          <a href="/#/login" class="font-medium text-emerald-400 hover:text-emerald-300">
            Sign in here
          </a>
        </p>
      </div>

      <!-- Oilfield-specific Info -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-lg p-4 border border-slate-700/50">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-emerald-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          <div class="text-sm text-slate-300">
            <p class="font-medium text-white">Why OilBuddy AI?</p>
            <p class="mt-1">
              {{ form.userType === 'contractor'
                ? 'Join thousands of contractors already using our platform to find jobs, track time, and get paid faster than ever before.'
                : form.userType === 'employee'
                  ? 'Sign up as a W-2 hand or supervisor with your company invite code. Pick Green Hat, White Hat, or Supervisor 1–3.'
                  : 'Streamline your oilfield operations with our comprehensive platform for job posting, crew management, and compliance tracking.'
              }}
            </p>
          </div>
        </div>
      </div>
      </div> <!-- Close register card -->
    </div> <!-- Close max-w-2xl container -->
  </div> <!-- Close main container -->
</template>

<script setup lang="ts">
import { buildApiUrl } from '@/config/api'
import type { RegisterForm, UserType } from '@/types'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Form state
const form = reactive<RegisterForm>({
  email: '',
  password: '',
  passwordConfirmation: '',
  firstName: '',
  lastName: '',
  userType: 'contractor' as UserType,
  companyName: '',
  acceptTerms: false
})

// Additional form fields
const formExtended = reactive({
  phone: '',
  ein: '',
  ssn: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  industry: '',
  companySize: '',
  inviteCode: '',
  crewLevel: '',
})

const crewLevelOptions = [
  { value: 'green_hat', label: 'Green Hat Hand', hint: 'New / green hat crew' },
  { value: 'white_hat', label: 'White Hat Hand', hint: 'Experienced hand' },
  { value: 'supervisor_1', label: 'Supervisor 1', hint: 'First-line supervisor' },
  { value: 'supervisor_2', label: 'Supervisor 2', hint: 'Mid-level supervisor' },
  { value: 'supervisor_3', label: 'Supervisor 3', hint: 'Senior supervisor' },
]

// UI state
const isLoading = ref(false)
const errorMessage = ref('')
const errors = reactive({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  companyName: '',
  ein: '',
  inviteCode: '',
  crewLevel: '',
})

// Methods
const selectUserType = (type: UserType | 'employee') => {
  form.userType = type as UserType
  if (type === 'contractor' || type === 'employee') {
    form.companyName = ''
  }
  if (type !== 'employee') {
    formExtended.inviteCode = ''
    formExtended.crewLevel = ''
  }
}

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })

  let isValid = true

  // Basic validation
  if (!form.firstName.trim()) {
    errors.firstName = 'First name is required'
    isValid = false
  }

  if (!form.lastName.trim()) {
    errors.lastName = 'Last name is required'
    isValid = false
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required'
    isValid = false
  } else if (!form.email.includes('@')) {
    errors.email = 'Please enter a valid email address'
    isValid = false
  }

  if (!form.password) {
    errors.password = 'Password is required'
    isValid = false
  } else if (form.password.length < 8) {
    errors.password = 'Password must be at least 8 characters'
    isValid = false
  }

  if (form.password !== form.passwordConfirmation) {
    errors.passwordConfirmation = 'Passwords do not match'
    isValid = false
  }

  // Company-specific validation
  if (form.userType === 'company') {
    if (!form.companyName?.trim()) {
      errors.companyName = 'Company name is required'
      isValid = false
    }
    if (!formExtended.ein?.trim()) {
      errors.ein = 'EIN is required for companies'
      isValid = false
    } else {
      // Basic EIN format validation (XX-XXXXXXX)
      const einPattern = /^\d{2}-\d{7}$/
      if (!einPattern.test(formExtended.ein.trim())) {
        errors.ein = 'EIN must be in format XX-XXXXXXX (e.g., 12-3456789)'
        isValid = false
      }
    }
  }

  if ((form.userType as string) === 'employee') {
    if (!formExtended.inviteCode?.trim()) {
      errors.inviteCode = 'Company invite code is required'
      isValid = false
    }
    if (!formExtended.crewLevel) {
      errors.crewLevel = 'Select Green Hat Hand, White Hat Hand, or Supervisor 1–3'
      isValid = false
    }
  }

  if (!form.acceptTerms) {
    errorMessage.value = 'You must accept the terms and conditions'
    isValid = false
  }

  return isValid
}

const handleRegister = async () => {
  if (!validateForm()) {
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    // Prepare registration data
    const registrationData = {
      ...form,
      ...formExtended
    }

    // Make API call to register endpoint
    const apiUrl = buildApiUrl('register.php')
    console.log('Making API call to:', apiUrl)
    console.log('Request data:', registrationData)
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registrationData)
    })

    const data = await response.json()

    if (response.ok && data.success) {
      // Store auth token and user data
      localStorage.setItem('auth_token', data.token)
      localStorage.setItem('user_type', data.user.userType)
      localStorage.setItem('user_data', JSON.stringify(data.user))
      
      console.log('Registration successful:', data)
      
      // Redirect to appropriate dashboard
      if (form.userType === 'company') {
        router.push('/company')
      } else if ((form.userType as string) === 'employee') {
        router.push('/employee')
      } else {
        router.push('/dashboard')
      }
    } else {
      // Handle API error response
      errorMessage.value = data.error || 'Registration failed. Please try again.'
    }
  } catch (error) {
    console.error('Registration error:', error)
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    })
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage.value = 'Network error: Unable to connect to the server. Please check if the API is running.'
    } else {
      errorMessage.value = `Network error occurred: ${error.message}`
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Text color overrides */
.text-oil-900 {
  @apply text-white;
}

.text-oil-600 {
  @apply text-slate-300;
}

.text-oil-700 {
  @apply text-slate-200;
}

.text-primary-600 {
  @apply text-emerald-400;
}

/* Card styles */
.card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50;
}

.card-body {
  @apply p-6;
}

/* Form styles */
.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50;
}

.btn-hero-primary {
  @apply w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}
</style>
